import { DataDictionaryType } from '../types/DataDictionaryType';
import { DataDictionaryTableType } from '../types/DataDictionaryType';
import { DataDictionaryTableRawType } from '../types/DataDictionaryType';
import { DataRecord } from './DataRecord';
import { DataFormatter } from '../dataformats/DataFormatter';
import { forEach } from 'mathjs';
import { Localizer } from '../localize/localizer';
import { DataTools } from './DataTools';
import { LogicEngine } from '../LogicEngine/LogicEngine';
import { DataServices } from '../utils/DataServices';
import { LangServices } from '../utils/LangServices';


export type FieldStatusType = 'warning' | 'error';
export interface FieldError
{
	field_name: string;
	error_message: string;
	field_status: FieldStatusType;
}

export class DataDictionaryTable
{
	rawDefinition: DataDictionaryTableType[] = [];
	tableName: string;

	/**
	 * Initialize a dictionary table wrapper
	 * @param theTableName
	 * @param initialObject
	 */
	constructor(theTableName: string, initialObject: DataDictionaryTableRawType[])
	{
		this.tableName = theTableName;
		if (initialObject.length > 0)
		{
			this.rawDefinition = initialObject[0].data;
		}
	}

	/**
	 * Generate a unique id with ###-###-#### format
	 */
	async generate3dash4(): Promise<string>
	{
		const dataService = DataServices.instance();
		let uuid;

		try
		{
			uuid = await dataService.generate3Dash4();
			return uuid;
		} catch (err)
		{
			console.log(err);
		}

		// TODO: Find a better algirthm that uses time to avoid duplicates
		// Generate a 10 digit ID
		uuid = ((Math.ceil(Math.random() * 1000000 + 1000) % 899) + 100).toString();
		uuid += '-' + ((Math.ceil(Math.random() * 1000000 + 1000) % 899) + 100).toString();
		uuid += '-' + ((Math.ceil(Math.random() * 1000000 + 1000) % 8999) + 1000).toString();

		return uuid;
	}

	/** Return the field given a field name
	 *
	 * @param fieldName A string value for field name to get field
	 *
	 * @returns field with the fieldName
	 */
	getField(fieldName: string): DataDictionaryTableType | undefined
	{
		for (let field of this.rawDefinition)
		{
			if (field.field === fieldName)
			{
				return field;
			}
		}
	}

	/**
	 * Return the type of field or "Text" by default
	 * @param fieldName the string value to get field
	 * @returns field type of the field
	 */
	getFieldType(fieldName: string): string
	{
		let field = this.getField(fieldName);
		if (field)
		{
			if (field.options)
			{
			}
			return field.field_type || "Text"
		}

		return 'Text';
	}

	/**
	 * Return the options of field or "" by default
	 * @param fieldName the string value to get field
	 * @returns field options of the field
	 */
	getFieldLookupName(fieldName: string): string
	{
		let field = this.getField(fieldName);
		if (field && field.options)
		{
			return field.options;
		}

		return '';
	}

	/**
	 * Return a blank record based on the fields in the data dictionary list
	 * @returns an object of values not a DataRecord
	 */
	getBlankObject(): any
	{
		let obj: any = {};
		for (let field of this.rawDefinition)
		{
			obj[field.field] = '';
			if (field.default_value) obj[field.field] = field.default_value;
		}

		return obj;
	}

	/**
	 * TODO not sure what this does for
	 * @param sourceValue A string value
	 * @param field
	 */
	equalTo(sourceValue: string, field: DataDictionaryTableType)
	{
		let targetValue = field.min_count ? field.min_count : '';
		if (sourceValue === targetValue) return true;

		return false;
	}

	/**
	 * Range Validation check based on the Year type field
	 * @param sourceValue A source string value to check
	 * @param field A target value to validate sourceValue in Range
	 * @returns true if sourceValue is in the range
	 */
	rangeValidationDecimal(sourceValue: string, field: DataDictionaryTableType): boolean
	{
		console.log(
			'RangeValidation on Decimal: ',
			field.min_count,
			'<=',
			sourceValue,
			'<=',
			field.max_count,
		);

		if (sourceValue === undefined || sourceValue === '') return false;

		let sourceYear = DataTools.getNumber(sourceValue);
		let min = DataTools.getNumber(field.min_count);
		let max = DataTools.getNumber(field.max_count);

		if (min && max && sourceYear)
		{
			if (sourceYear < min) return false;
			if (sourceYear > max) return false;
			return true;
		} else
		{
			console.warn(
				'Data Dictionary page : unable to year validation range due to invalid values in ',
				field,
			);
		}

		return true;
	}

	/**
	 * Range Validation based on a date.
	 * @param sourceValue A string value to check
	 * @param field A targeValue to validate sourceValue in date range
	 * @returns true if sourceValue is in date range
	 */
	rangeValidationDate(sourceValue: string, field: DataDictionaryTableType): boolean
	{
		console.log(
			'RangeValidation Date ',
			field.min_count,
			'<=',
			sourceValue,
			'<=',
			field.max_count,
		);

		if (sourceValue === undefined || sourceValue === '') return false;

		let sourceDate = DataTools.getMoment(sourceValue);
		let min = DataTools.getMoment(field.min_count);
		let max = DataTools.getMoment(field.max_count);

		if (min && max && sourceDate)
		{
			let minDate = min.toDate();
			let maxDate = max.toDate();
			let myDate = sourceDate.toDate();
			console.log('Date range validation ', minDate, ' < ', myDate, ' < ', maxDate);
			if (minDate < myDate && myDate < maxDate)
			{
				return true;
			} else
			{
				return false;
			}
		} else
		{
			console.log(
				'DataDictionary rangeValidation invalid dates',
				sourceValue,
				field.min_count,
				field.max_count,
			);
		}

		return true;
	}

	/**
	 * Validate the source data is within a given range
	 * @param sourceValue A string source value to check
	 * @param field A targetValue to validate sourceValue in range
	 * @returns true if sourceValue is in range
	 */
	rangeValidation(sourceValue: string, field: DataDictionaryTableType): boolean
	{
		if (field.field_type === 'Year') return this.rangeValidationDecimal(sourceValue, field);

		if (field.field_type === 'Date') return this.rangeValidationDate(sourceValue, field);

		// Default type
		return this.rangeValidationDecimal(sourceValue, field);
	}

	/**
	 * Review all fields in the data dictionary and validate the data
	 * is within the acceptable inputs
	 * @param record
	 * @param fieldsEnabled
	 * @returns A FieldError[] promise
	 */
	async validateRecord(
		record: DataRecord,
		fieldsEnabled: { [fieldName: string]: boolean },
	): Promise<FieldError[]>
	{
		record.validateId();
		const df = new DataFormatter();
		let results: FieldError[] = [];

		for (let field of this.rawDefinition)
		{
			// If the field is specifically disabled, do not validate it
			if (fieldsEnabled[field.field] != undefined && !fieldsEnabled[field.field]) continue;

			let value = record.data[field.field];

			if (!field.validation_name) field.validation_name = '';

			if (field.field_type === 'TextParticipantId' && !value)
			{
				record.data[field.field] = await this.generate3dash4();
				value = record.data[field.field];
			} else if (field.validation_name.toLowerCase() === 'calculation')
			{
				// Calculations are done later
			} else
			{
				/** Placeholder, does simple validation on fields */
				if (field.validation_name !== '')
				{
					// let testValue = df.validateData(field.field_type, value, record.data);
					let testValue = value;

					if (field.validation_name === 'PatternMatch')
					{
						if (field.options)
						{
							const patternText = field.options;
							const re = new RegExp(patternText, 'i');
							if (!re.test(value))
							{
								results.push({
									field_name: field.field,
									error_message:
										LangServices.T(Localizer.Instance().errorPatternFail) +
										' [' +
										patternText +
										']',
									field_status: 'error',
								});
							}
						} else
						{
							console.warn(
								'Data Dictionary page - PatternMatch validation for ',
								field,
								' missing options.',
							);
						}
					} else if (field.validation_name === 'Equal To')
					{
						if (!this.equalTo(testValue, field))
						{
							results.push({
								field_name: field.field,
								error_message: LangServices.T(Localizer.Instance().errorEqualTo) + field.min_count,
								field_status: 'error',
							});
						}
					} else if (field.validation_name === 'Range')
					{
						if (!this.rangeValidation(testValue, field))
						{
							results.push({
								field_name: field.field,
								error_message: LangServices.T(Localizer.Instance().errorRangeFail),
								field_status: 'error',
							});
						}
					} else if (field.validation_name.replace(" ", "").toLowerCase() === 'notempty')
					{
						// console.log("Check name=", field.field, "value=[", value, "] testValue=[", testValue, "] type=", field.field_type, " record=", record.data[field.field]);
						if (testValue === undefined || testValue === null || testValue === '')
						{
							results.push({
								field_name: field.field,
								error_message: LangServices.T(Localizer.Instance().errorFieldMissing),
								field_status: 'error',
							});
						}
					} else
					{
						console.log('Missing validation for type:', testValue, {
							validation_name: field.validation_name,
							type: field.field_type,
							value: value,
							options: field.options,
						});
					}
				}
			}
		}

		return results;
	}
}

/**
 * Wrapper around the Master Data Dictionary
 * @category Data and API Services
 */
export class DataDictionary
{
	rawDataDictionary: DataDictionaryType;
	tables: { [tableName: string]: DataDictionaryTable } = {};

	/** Initialize class with the object after it comes from the CDN Server */
	constructor(initialObject: DataDictionaryType)
	{
		this.rawDataDictionary = initialObject;

		if (!this.rawDataDictionary.tables)
		{
			console.warn(
				'Data Dictionary page - constructor - initializing DataDictionary with a bad definition set.',
			);
			return;
		}

		const tableNames = Object.keys(this.rawDataDictionary.tables);
		tableNames.forEach((tableName) =>
		{
			this.tables[tableName] = new DataDictionaryTable(
				tableName,
				this.rawDataDictionary.tables[tableName],
			);
		});
	}

	/**
	 * Return the lookup table
	 * @param optionName A string value to get lookup
	 * @returns A lookup table
	 */
	getLookupTable(optionName: string)
	{
		optionName = optionName.trim().toLocaleLowerCase();
		for (let name of Object.keys(this.rawDataDictionary.lookup))
		{
			if (name.trim().toLocaleLowerCase() === optionName)
				return this.rawDataDictionary.lookup[name];
		}

		return [];
	}
	/**
	 * Get English value from Lookup
	 * @param fieldName
	 * @param value
	 * @returns
	 */
	getTextFromLookUp(fieldName: string, value: any)
	{
		if (value === '') return '';
		const list = this.getLookupTable(fieldName);
		// Check for language setting
		const langCodeShort = LangServices.currentLanguage.short_code.toLowerCase();
		const langCode = LangServices.currentLanguage.code.toLowerCase();

		for (const item of list)
		{
			if (item.code == value || item.english == value)
			{
				if ((item as any)[`text_${langCode}`])
				{
					return (item as any)[`text_${langCode}`];
				}
				return (item as any)[`text_${langCodeShort}`];
			}
		}

		const strValue = value + "";
		for (const item of list)
		{
			if (item.code == strValue || item.english == strValue)
			{
				if ((item as any)[`text_${langCode}`])
				{
					return (item as any)[`text_${langCode}`];
				}
				return (item as any)[`text_${langCodeShort}`];
			}
		}

		return '';
	}
	/**
	 * Convert a code or text based on a lookup table to the text
	 * @param fieldName A filed name value to get text
	 * @param value A value to check
	 * @returns A text if it finds equal
	 */
	getLookupAsText(fieldName: string, value: any, tableName?: string): string
	{

		if (typeof (value) === "string" && value === "") return "";

		let text = this.getTextFromLookUp(fieldName, value);
		if (text) return text;
		// If tableName is provided and valid
		if (tableName && tableName in this.tables)
		{
			const table: DataDictionaryTable = this.tables[tableName];
			for (const field of table.rawDefinition)
			{
				// Check if field exists in the table and options exists
				if (field.field === fieldName && field.options)
				{
					text = this.getTextFromLookUp(field.options, value);
					if (text) return text;
				}
			}
		}

		if (value === "null" || value === "NULL" || value === '"null"')
			return "";

		if (value === "")
			return "";

		return 'Unknown Code:' + value;
	}
	/**
	 * Get Code from Lookup
	 * @param fieldName
	 * @param value
	 * @returns
	 */
	getCodeFromLookUp(fieldName: string, value: any): string
	{
		if (value === '') return '';

		const list = this.getLookupTable(fieldName);
		for (const item of list)
		{
			if (item.code == value || item.english == value)
			{
				return item.code;
			}
		}

		for (const item of list)
		{
			if (item.code + "" == value + "" || item.english + "" == value + "")
			{
				return item.code;
			}
		}

		return '';
	}
	/**
	 * Convert a code or text based on a lookup table to the code
	 * @param fieldName A string value to find code
	 * @param value A value to check
	 * @returns A code if it finds equal
	 */
	getLookupAsCode(fieldName: string, value: any, tableName?: string): string
	{
		if (!value) return '';
		let code = this.getCodeFromLookUp(fieldName, value);
		if (code) return code;
		// Check if tableName is passed and valid
		if (tableName && tableName in this.tables)
		{
			const table: DataDictionaryTable = this.tables[tableName];
			for (const field of table.rawDefinition)
			{
				// Check if field exists in the table and options exists
				if (field.field === fieldName && field.options)
				{
					code = this.getCodeFromLookUp(field.options, value);
					if (code) return code;
				}
			}
		}

		if (value === "null" || value === "NULL" || value === '"null"')
			return "";

		if (value === "")
			return "";

		return 'Unknown Code:' + value;
	}

	/**
	 * Allocate a DataRecord for this table from a given raw object
	 *
	 * @param tableName A string value for table name
	 * @param raw object that contains the fields to be save.
	 * @returns A DataRecord object
	 */
	getRecordFromObject(tableName: string, raw: any)
	{
		let initialFields: any = {};

		if (!this.tables[tableName])
		{
			console.error('Data Dictionary page - getRecordFromObject - missing table ', tableName);
		} else
		{
			initialFields = this.tables[tableName].getBlankObject();
		}

		Object.assign(initialFields, raw);
		const rec = new DataRecord(tableName, initialFields);
		return rec;
	}

	/**
	 * Find a table or throw an error.
	 *
	 * @param tableName The name of the table you want
	 * @returns A reference to the table helper
	 */
	getTable(tableName: string): DataDictionaryTable
	{
		if (tableName === "")
		{
			console.log("DataDictionary getTable called with '' table name.");
		}

		if (tableName.startsWith("#") || tableName === "")
		{
			/* Special Case for In-Memory Temp Tables */

			if (!this.tables[tableName])
			{
				this.tables[tableName] = new DataDictionaryTable(
					tableName,
					[
						{
							"tableName": tableName,
							"data": [{
								"field": "id",
								"field_label_en": "Unique Event ID",
								"field_type": "ID",
								"validation_name": '',
								"related_table": null,
								"default_value": 0,
								"options": '',
								"min_count": 0,
								"max_count": 0,
								"blinded": null,
								"pii": null,
								"view_group": null,
								"edit_group": null,
								"export_id": "ID"
							}]
						}
					]
				);
			}

		}

		// Check for exact match
		if (this.tables[tableName])
			return this.tables[tableName];

		// Check for bad case match
		for (const name of Object.keys(this.tables))
		{
			if (name.toLowerCase() == tableName.toLowerCase())
				return this.tables[name];
		}

		console.warn(`Data Dictionary page - getTable - Missing table request '${tableName}'`);
		throw `Missing table requested: ${tableName}`;
	}
}
